// eslint-disable-next-line no-restricted-imports
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import type { FC } from "react";

import { CourseSettingsCourseInfoForm } from "components/forms/CourseSettingsCourseInfoForm";
import { CourseSettingsGeneralForm } from "components/forms/CourseSettingsGeneralForm";
import { CourseSettingsGradeForm } from "components/forms/CourseSettingsGradeForm";
import { CourseSettingsVirtualClassForm } from "components/forms/CourseSettingsVirtualClassForm";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { IconText } from "mds/components/IconText";
import { AssessPointsMarkerIcon, BookCourseIcon, GearSettingsIcon, VideoIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCourseSettingsTabIndex } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { isAVEnabled } from "utils/waffle-flags";

// Used to open the Course grade tab from the CourseGradeCard.
export const COURSE_SETTINGS_GRADE_TAB_INDEX = 2;

export const CourseSettingsModal: FC = () => {
  const dispatch = useAppDispatch();
  const tabIndex = useAppSelector(selectCourseSettingsTabIndex);

  const closeCourseSettingsModal = () => {
    dispatch(viewStateActions.setCourseSettingsTabIndex(undefined));
  };

  const tabClassName =
    "group/tab -mb-0.5 border-b-2 border-blue-tint-90 py-3 text-black-tint-20 hover:border-blue-tint-70 data-[selected]:border-blue";

  const dialogPanelClassName = isAVEnabled() ? "w-[500px]" : "w-[400px]";

  return (
    <Dialog
      // Course settings modal needs to appear above screenshare
      className="z-[52]"
      dialogPanelClassName={dialogPanelClassName}
      open={tabIndex !== undefined}
      title={<span className="font-bold">{t("settings.course_settings")}</span>}
      onClose={closeCourseSettingsModal}
    >
      {/*
      I (David) tried to use the `<TabBar>` component from MDS for this tab bar,
      but found a strange bug: if you switch to the Course Score tab, fill out
      the form with invalid values (e.g. percentages that do not add up to 100),
      and attempt to submit, then the form does *not* display the error message, as it
      is supposed to. Instead, it resets the values in the form to the existing values
      from the course, with no visual feedback at all. I suspect this is due to some kind
      of re-rendering issue with the `<TabBar>` component, but I couldn't figure out
      how to fix it. So I am using the tab components from Headless UI instead,
      which do not have this issue.
      (`<TabBar>` is a wrapper around the Headless UI tab components, anyway.)
      */}
      <TabGroup
        selectedIndex={tabIndex}
        onChange={(index) => dispatch(viewStateActions.setCourseSettingsTabIndex(index))}
      >
        <TabList className="flex flex-row gap-8 border-b-2 border-blue-tint-90 text-sm font-medium">
          <Tab className={tabClassName}>
            <IconText
              iconStart={<GearSettingsIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("settings.general")}
            />
          </Tab>
          <Tab className={tabClassName}>
            <IconText
              iconStart={<BookCourseIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.course_info")}
            />
          </Tab>
          <Tab className={tabClassName}>
            <IconText
              iconStart={<AssessPointsMarkerIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("settings.course_grade")}
            />
          </Tab>
          {isAVEnabled() && (
            <Tab className={tabClassName}>
              <IconText
                iconStart={<VideoIcon className="group-data-[selected]/tab:text-blue" />}
                text={t("glossary.virtual_class")}
              />
            </Tab>
          )}
        </TabList>
        <TabPanels>
          <TabPanel>
            <CourseSettingsGeneralForm className="pt-5" onClose={closeCourseSettingsModal} />
          </TabPanel>
          <TabPanel>
            <CourseSettingsCourseInfoForm className="pt-5" onClose={closeCourseSettingsModal} />
          </TabPanel>
          <TabPanel>
            <CourseSettingsGradeForm className="pt-5" onClose={closeCourseSettingsModal} />
          </TabPanel>
          {isAVEnabled() && (
            <TabPanel>
              <CourseSettingsVirtualClassForm className="pt-5" onClose={closeCourseSettingsModal} />
            </TabPanel>
          )}
        </TabPanels>
      </TabGroup>
    </Dialog>
  );
};
